import api from './client';

// ==================== TYPES ====================

// Type pour les templates de documents simples (.docx dans media/documents/templates)
export interface SimpleDocumentTemplate {
  id: string;
  name: string;
  fileName: string;
  relative_path?: string;  // Chemin relatif pour download/view (supporte les sous-dossiers)
  category?: string;  // Catégorie du document (templates, avenants, etc.)
  date: string;
  size: string;
  url: string;
}

export interface DocumentTemplate {
  id: number;
  name: string;
  code: string;
  category: 'CONTRAT' | 'AVENANT' | 'ATTESTATION' | 'AUTRE';
  category_display: string;
  type: string;
  type_display: string;
  genre: 'M' | 'F' | 'NEUTRE';
  description: string;
  is_active: boolean;
  version: string;
  created_at: string;
}

export interface Document {
  id: number;
  document_number: string;
  employee: number;
  employee_name: string;
  employee_id: string;
  template: number;
  template_name: string;
  template_type: string;
  status: 'DRAFT' | 'GENERATED' | 'SENT' | 'SIGNED' | 'ARCHIVED' | 'CANCELLED';
  status_display: string;
  is_active: boolean;
  date_effet?: string;
  date_fin?: string;
  date_signature?: string;
  created_at: string;
  updated_at: string;
  generated_document?: string;
  signed_document?: string;
  contrat_reference_number?: string;
}

export interface DocumentDetail extends Document {
  document_data: Record<string, any>;
  created_by_name: string;
  notes?: string;
}

export interface DocumentGeneratePayload {
  employee: number;
  template: number;
  contrat_reference?: number | null;
  date_effet?: string;
  date_fin?: string;
  date_signature?: string;
}

export interface DocumentStats {
  total: number;
  signed: number;
  pending: number;
  this_month: number;
}

// ==================== API CALLS ====================

// Templates
export async function fetchTemplates(params?: {
  category?: string;
  type?: string;
  is_active?: boolean;
}): Promise<DocumentTemplate[]> {
  const { data } = await api.get('contracts/templates/', { params });
  return data;
}

export async function fetchTemplateDetail(id: number): Promise<DocumentTemplate> {
  const { data } = await api.get(`contracts/templates/${id}/`);
  return data;
}

// Documents
export async function fetchDocuments(params?: {
  employee?: number;
  template?: number;
  status?: string;
  date_after?: string;
  date_before?: string;
  search?: string;
}): Promise<Document[]> {
  const { data } = await api.get('contracts/documents/', { params });
  return data;
}

export async function fetchDocumentDetail(id: number): Promise<DocumentDetail> {
  const { data } = await api.get(`contracts/documents/${id}/`);
  return data;
}

export async function generateDocument(payload: DocumentGeneratePayload) {
  const { data } = await api.post('contracts/documents/generate/', payload);
  return data;
}

export async function downloadDocument(id: number, type: 'generated' | 'signed' = 'generated') {
  const endpoint = type === 'signed' ? 'download_signed' : 'download';
  const { data } = await api.get(`contracts/documents/${id}/${endpoint}/`, {
    responseType: 'blob',
  });
  return data;
}

export async function uploadSignedDocument(id: number, file: File) {
  const formData = new FormData();
  formData.append('signed_document', file);
  const { data } = await api.post(`contracts/documents/${id}/upload_signed/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateDocumentStatus(id: number, status: Document['status']) {
  const { data } = await api.patch(`contracts/documents/${id}/`, { status });
  return data;
}

// ==================== API SIMPLE TEMPLATES ====================

/**
 * Récupère la liste de tous les templates de documents simples (.docx)
 */
export async function fetchSimpleDocumentTemplates(): Promise<SimpleDocumentTemplate[]> {
  const { data } = await api.get('documents/templates/');
  // L'API retourne {templates: [...], analysis: {...}}
  return data.templates || data;
}

/**
 * Téléverse un nouveau template de document simple
 */
export async function uploadSimpleDocumentTemplate(file: File, customName?: string): Promise<SimpleDocumentTemplate> {
  const formData = new FormData();
  formData.append('file', file);
  
  if (customName) {
    formData.append('name', customName);
  }
  
  const { data } = await api.post<SimpleDocumentTemplate>('documents/templates/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return data;
}

/**
 * Télécharge un template de document simple
 */
export async function downloadSimpleDocumentTemplate(filename: string): Promise<Blob> {
  const path = filename.includes('/') ? filename : `templates/${filename}`;
  const { data } = await api.get(`documents/${encodeURIComponent(path)}/download/`, {
    responseType: 'blob',
  });
  
  return data;
}

/**
 * Supprime un template de document simple
 */
/**
 * Supprime un template de document simple
 */
export async function deleteSimpleDocumentTemplate(filename: string): Promise<void> {
  const path = filename.includes('/') ? filename : `templates/${filename}`;
  await api.delete(`documents/${encodeURIComponent(path)}/`);
}

/**
 * Met à jour un template de document (renommer et/ou remplacer le fichier)
 */
export async function updateSimpleDocumentTemplate(
  oldFilename: string,
  newName?: string,
  newFile?: File
): Promise<SimpleDocumentTemplate> {
  const formData = new FormData();
  
  if (newName) {
    formData.append('name', newName);
  }
  
  if (newFile) {
    formData.append('file', newFile);
  }
  
  const path = oldFilename.includes('/') ? oldFilename : `templates/${oldFilename}`;
  const { data } = await api.put<SimpleDocumentTemplate>(
    `documents/${encodeURIComponent(path)}/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
}

/**
 * Utilitaire pour déclencher le téléchargement d'un fichier
 */
export function triggerFileDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
export async function deleteDocument(id: number) {
  await api.delete(`contracts/documents/${id}/`);
}

// Stats
export const fetchDocumentStats = async (): Promise<DocumentStats> => {
  // Pas de slash initial pour respecter le baseURL (…/api/)
  const response = await api.get<DocumentStats>('contracts/documents/statistics/');
  return response.data;
};
